import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { verificationService } from '../../../services/verificationService';
import { accessRequestService } from '../../../services/accessRequestService';

const ApplicationStatusPanel = ({ email }) => {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    loadRequestStatus();

    // Subscribe to real-time updates for this email
    const channel = verificationService?.subscribeToRequests((payload) => {
      if (payload?.eventType === 'UPDATE' && payload?.new?.email === email) {
        setRequest(transformRequest(payload?.new));
      }
    });

    setRealtimeChannel(channel);

    return () => {
      if (channel) {
        verificationService?.unsubscribe(channel);
      }
    };
  }, [email]);

  const transformRequest = (dbRow) => {
    return {
      id: dbRow?.id,
      fullName: dbRow?.full_name,
      email: dbRow?.email,
      role: dbRow?.role,
      status: dbRow?.status,
      submittedAt: dbRow?.submitted_at,
      reviewedAt: dbRow?.reviewed_at,
      lastStatusChange: dbRow?.last_status_change,
      verificationScore: dbRow?.verification_score,
      rejectionReason: dbRow?.rejection_reason
    };
  };

  const loadRequestStatus = async () => {
    try {
      setLoading(true);
      const requestData = await accessRequestService?.getByEmail(email);
      setRequest(requestData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = () => {
    const configs = {
      pending: {
        icon: 'Clock',
        color: 'text-warning',
        bg: 'bg-warning/10',
        border: 'border-warning/20',
        title: 'Application Pending',
        message: 'Your application is awaiting review by our team. We will notify you once it has been reviewed.'
      },
      under_review: {
        icon: 'Eye',
        color: 'text-primary',
        bg: 'bg-primary/10',
        border: 'border-primary/20',
        title: 'Under Review',
        message: 'Your application is currently being reviewed by our team. This process typically takes 1-2 business days.'
      },
      approved: {
        icon: 'CheckCircle',
        color: 'text-success',
        bg: 'bg-success/10',
        border: 'border-success/20',
        title: 'Application Approved',
        message: 'Congratulations! Your application has been approved. You can now access the platform.'
      },
      rejected: {
        icon: 'XCircle',
        color: 'text-error',
        bg: 'bg-error/10',
        border: 'border-error/20',
        title: 'Application Not Approved',
        message: 'Unfortunately, your application was not approved at this time.'
      }
    };
    return configs?.[request?.status] || configs?.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-8 elevation-sm border border-border text-center">
        <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Loading application status...</p>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="bg-card rounded-2xl p-8 elevation-sm border border-border text-center">
        <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Unable to load application status</p>
      </div>
    );
  }

  const statusConfig = getStatusConfig();

  return (
    <div className="bg-card rounded-2xl p-6 md:p-8 elevation-sm border border-border">
      {/* Status Header */}
      <div className={`rounded-xl p-6 mb-6 border ${statusConfig?.bg} ${statusConfig?.border}`}>
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl bg-background/50`}>
            <Icon name={statusConfig?.icon} size={32} className={statusConfig?.color} />
          </div>
          <div className="flex-1">
            <h3 className={`text-xl font-bold mb-2 ${statusConfig?.color}`}>
              {statusConfig?.title}
            </h3>
            <p className="text-foreground mb-4">{statusConfig?.message}</p>
            {request?.rejectionReason && request?.status === 'rejected' && (
              <div className="bg-background/50 rounded-lg p-4 mt-4">
                <p className="text-sm font-medium text-foreground mb-1">Reason:</p>
                <p className="text-sm text-muted-foreground">{request?.rejectionReason}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Application Details */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-foreground mb-4">Application Details</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-muted/50 rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Application ID</p>
            <p className="text-sm font-mono text-foreground">{request?.id?.slice(0, 8)}...</p>
          </div>
          
          <div className="bg-muted/50 rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Role</p>
            <p className="text-sm font-medium text-foreground capitalize">{request?.role}</p>
          </div>
          
          <div className="bg-muted/50 rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Submitted</p>
            <p className="text-sm font-medium text-foreground">{formatDate(request?.submittedAt)}</p>
          </div>
          
          {request?.lastStatusChange && (
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-1">Last Updated</p>
              <p className="text-sm font-medium text-foreground">{formatDate(request?.lastStatusChange)}</p>
            </div>
          )}

          {request?.verificationScore && (
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-1">Verification Score</p>
              <p className="text-sm font-medium text-foreground">{request?.verificationScore}/100</p>
            </div>
          )}
        </div>

        {/* Status Timeline */}
        <div className="bg-muted/50 rounded-xl p-6 mt-6">
          <h5 className="text-sm font-semibold text-foreground mb-4">Application Timeline</h5>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="Check" size={16} className="text-success" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Application Submitted</p>
                <p className="text-xs text-muted-foreground">{formatDate(request?.submittedAt)}</p>
              </div>
            </div>

            {request?.status === 'under_review' && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="Eye" size={16} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Under Review</p>
                  <p className="text-xs text-muted-foreground">{formatDate(request?.lastStatusChange)}</p>
                </div>
              </div>
            )}

            {(request?.status === 'approved' || request?.status === 'rejected') && (
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full ${statusConfig?.bg} flex items-center justify-center`}>
                  <Icon name={statusConfig?.icon} size={16} className={statusConfig?.color} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground capitalize">{request?.status}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(request?.reviewedAt)}</p>
                </div>
              </div>
            )}

            {request?.status === 'pending' && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Awaiting Review</p>
                  <p className="text-xs text-muted-foreground">Pending admin review</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-primary/5 rounded-xl p-6 mt-6 border border-primary/10">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-1" />
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Need Help?</p>
              <p className="text-sm text-muted-foreground">
                If you have questions about your application status, please contact our support team.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatusPanel;